import {
  PDFCheckBox,
  PDFDocument,
  PDFRef,
  PDFTextField,
  StandardFonts,
} from "pdf-lib";
import type {
  ContractPdfData,
  PdfGenerationResult,
  PdfTemplateType,
} from "../../types/pdf";
import { formatDateForPdf, formatDateForPdfShortYear } from "./fieldMapper";

const PDF_TEMPLATE_INTEGRITY_FAILED = "PDF_TEMPLATE_INTEGRITY_FAILED";
const PDF_FILLED_OUTPUT_INVALID = "PDF_FILLED_OUTPUT_INVALID";
const PDF_FIELD_APPEARANCE_FAILED = "PDF_FIELD_APPEARANCE_FAILED";
const PDF_FLATTEN_FAILED = "PDF_FLATTEN_FAILED";

interface TemplateIntegrityResult {
  valid: boolean;
  issues: string[];
  fieldCount: number;
  visibleWidgetCount: number;
}

interface FilledOutputValidationResult {
  valid: boolean;
  issues: string[];
}

/**
 * Helper function to check if a contract type is a gas contract
 * Matches: "gas" as separate word, or "erdgas"/"naturgas" anywhere in the name
 */
function isGasContract(contractTypeName: string): boolean {
  const lowerName = contractTypeName.toLowerCase();

  // Check if contains "erdgas" anywhere (e.g., "Erdgas", "Erdgas24", "Erdgasfix24")
  if (lowerName.includes("erdgas")) {
    return true;
  }

  // Check if contains "naturgas" anywhere (e.g., "Naturgas", "Naturgas24")
  if (lowerName.includes("naturgas")) {
    return true;
  }

  // Check if "gas" appears as a standalone word (word boundary check)
  // Matches "Gas", "Gasag Gas Fix 24" but NOT "Megastream"
  if (/\bgas\b/i.test(contractTypeName)) {
    return true;
  }

  return false;
}

/**
 * Determines which PDF template to use based on contract type
 * Checks for "Gas" keyword to use Gas template, otherwise uses Strom template
 */
export function determinePdfTemplate(contractType: string): {
  templatePath: string;
  templateType: PdfTemplateType;
} {
  // Check if contract is a gas contract using improved logic
  const isGas = isGasContract(contractType);

  console.log("Determining PDF template for:", contractType);
  console.log("Is Gas contract:", isGas);

  return {
    templatePath: isGas
      ? "/empty_contract_pdfs/AUFTRAG_GAS.pdf"
      : "/empty_contract_pdfs/AUFTRAG_STROM.pdf",
    templateType: isGas ? "AUFTRAG_GAS" : "AUFTRAG_STROM",
  };
}

const inferTemplateType = (
  templatePath: string,
  contractType: string
): PdfTemplateType => {
  const loweredPath = templatePath.toLowerCase();
  if (loweredPath.includes("auftrag_gas") || loweredPath.includes("gas")) {
    return "AUFTRAG_GAS";
  }

  if (loweredPath.includes("auftrag_strom") || loweredPath.includes("strom")) {
    return "AUFTRAG_STROM";
  }

  return determinePdfTemplate(contractType).templateType;
};

const buildPdfFailure = (
  templateType: PdfTemplateType,
  errorCode: string,
  error: string,
  errorDetails?: string
): PdfGenerationResult => ({
  success: false,
  error,
  errorCode,
  errorDetails,
  templateType,
});

const extractErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const collectVisibleWidgetRefs = (pdfDoc: PDFDocument): Set<string> => {
  const refs = new Set<string>();

  for (const page of pdfDoc.getPages()) {
    const pageNode = (page as unknown as { node?: { Annots?: () => unknown } })
      .node;
    const annots = pageNode?.Annots?.() as
      | {
          size: () => number;
          get: (index: number) => unknown;
        }
      | undefined;

    if (!annots) {
      continue;
    }

    for (let index = 0; index < annots.size(); index++) {
      const annotRef = annots.get(index);
      if (annotRef instanceof PDFRef) {
        refs.add(String(annotRef));
      }
    }
  }

  return refs;
};

/**
 * Validates that top-level AcroForm fields point at visible page widgets.
 * This prevents silent writing into detached/orphan field refs.
 */
export function validateTemplateIntegrity(
  pdfDoc: PDFDocument,
  templateType: PdfTemplateType
): TemplateIntegrityResult {
  const form = pdfDoc.getForm();
  const fields = form.getFields();
  const fieldCount = fields.length;
  const visibleWidgetRefs = collectVisibleWidgetRefs(pdfDoc);
  const visibleWidgetCount = visibleWidgetRefs.size;

  const issues: string[] = [];

  if (fieldCount === 0) {
    issues.push("No AcroForm fields found");
  }

  if (visibleWidgetCount === 0) {
    issues.push("No page widget annotations found");
  }

  const seenFieldNames = new Set<string>();
  const duplicateFieldNames = new Set<string>();
  const orphanTopLevelRefs: string[] = [];

  for (const field of fields) {
    const fieldName = field.getName();
    if (seenFieldNames.has(fieldName)) {
      duplicateFieldNames.add(fieldName);
    }
    seenFieldNames.add(fieldName);

    const fieldRef = (field as unknown as { ref?: unknown }).ref;
    if (!(fieldRef instanceof PDFRef)) {
      issues.push(`Field ${fieldName} has no indirect ref`);
      continue;
    }

    const fieldRefText = String(fieldRef);
    if (!visibleWidgetRefs.has(fieldRefText)) {
      orphanTopLevelRefs.push(fieldRefText);
    }
  }

  if (duplicateFieldNames.size > 0) {
    issues.push(
      `Duplicate field names in AcroForm: ${[...duplicateFieldNames].join(
        ", "
      )}`
    );
  }

  if (orphanTopLevelRefs.length > 0) {
    issues.push(
      `Top-level field refs missing from page annotations: ${orphanTopLevelRefs.join(
        ", "
      )}`
    );
  }

  const valid = issues.length === 0;

  console.log("PDF template integrity check", {
    templateType,
    valid,
    fieldCount,
    visibleWidgetCount,
    issueCount: issues.length,
  });

  return {
    valid,
    issues,
    fieldCount,
    visibleWidgetCount,
  };
}

/**
 * Ensures field mapping writes were actually applied.
 */
export function validateFilledOutput(params: {
  attemptedFieldCount: number;
  filledFieldCount: number;
  missingFieldNames: string[];
  fillErrors: string[];
}): FilledOutputValidationResult {
  const issues: string[] = [];

  if (params.attemptedFieldCount === 0) {
    issues.push("No mapped fields were attempted");
  }

  if (params.filledFieldCount === 0 && params.attemptedFieldCount > 0) {
    issues.push("No mapped fields were filled");
  }

  if (params.missingFieldNames.length > 0) {
    issues.push(
      `Mapped fields missing in PDF: ${params.missingFieldNames.join(", ")}`
    );
  }

  if (params.fillErrors.length > 0) {
    issues.push(`Field fill errors: ${params.fillErrors.join(" | ")}`);
  }

  return {
    valid: issues.length === 0,
    issues,
  };
}

/**
 * Loads a PDF template from the public directory
 */
async function loadPdfTemplate(templatePath: string): Promise<ArrayBuffer> {
  const response = await fetch(templatePath);
  if (!response.ok) {
    throw new Error(`Failed to load PDF template: ${templatePath}`);
  }
  return await response.arrayBuffer();
}

/**
 * Converts base64 data URL to ArrayBuffer for embedding in PDF
 */
function dataUrlToArrayBuffer(dataUrl: string): Uint8Array {
  // Extract base64 data (remove "data:image/png;base64," prefix if present)
  const base64Data = dataUrl.includes(",")
    ? dataUrl.split(",")[1] || ""
    : dataUrl;

  // Decode base64 to binary string
  const binaryString = atob(base64Data);

  // Convert binary string to Uint8Array
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
}

/**
 * Loads the field mapping JSON file for the contract type
 */
async function loadFieldMapping(
  contractType: string
): Promise<Record<string, string>> {
  const isGas = isGasContract(contractType);
  const mappingPath = isGas
    ? "/empty_contract_pdfs/pdf-field-mapping_gas.json"
    : "/empty_contract_pdfs/pdf-field-mapping_strom.json";

  console.log("Loading field mapping from:", mappingPath);

  const response = await fetch(mappingPath);
  if (!response.ok) {
    throw new Error(`Failed to load field mapping: ${mappingPath}`);
  }
  return await response.json();
}

/**
 * Maps contract data to PDF field values using the field mapping
 */
function mapDataToFields(
  data: ContractPdfData,
  fieldMapping: Record<string, string>,
  contractType: string
): Record<string, string> {
  const isGas = isGasContract(contractType);
  const fieldsToFill: Record<string, string> = {};

  // Helper function to safely add field if mapping exists
  const addField = (mappingKey: string, value: string | undefined) => {
    const fieldName = fieldMapping[mappingKey];
    if (value && fieldName) {
      fieldsToFill[fieldName] = value;
    }
  };

  // Calculate full address
  const fullAddress =
    `${data.customer.address.street} ${data.customer.address.houseNumber}`.trim();

  // Calculate ortDatum (city, date)
  const ortDatum = `${data.customer.address.city}, ${data.selectedDate}`.trim();

  // Calculate lastNameFirstName format
  const lastNameFirstName =
    `${data.customer.lastName}, ${data.customer.firstName}`.trim();

  // Calculate zipCodeCity format (zipCode, city)
  const zipCodeCity =
    `${data.customer.address.zipCode}, ${data.customer.address.city}`.trim();

  // General fields
  addField("salesPartnerCompany", data.salesPartnerCompany);
  addField("salesEmployeeName", data.salesEmployeeName);
  addField("firstName", data.customer.firstName);
  addField("lastNameSimple", data.customer.lastName); // a5 - lastName only
  addField("birthdate", data.customer.birthdate);
  addField("email", data.customer.email);
  addField("streetAddress", fullAddress); // a10 - street + house number
  addField("zipCode", zipCodeCity); // a11 - "zipCode, city"
  addField("phone", data.customer.phone);

  // SEPA mandate fields - only add if IBAN is present
  if (data.bankDetails && data.bankDetails.trim() !== "") {
    addField("lastNameFirstName", lastNameFirstName); // a26 - "lastName, firstName"
    addField("street", fullAddress); // a27 - street + house number (duplicate)
    addField("city", zipCodeCity); // a28 - "zipCode, city"
    addField("iban", data.bankDetails); // a31
  }

  // Contract signature date - always add (used for "Ort und Datum" at bottom)
  addField("ortDatum", ortDatum); // a32 - "city, dd.mm.yyyy"

  // Invoice fields (optional)
  addField("invoiceRecipient", data.invoiceRecipient);
  addField("invoiceStreet", data.invoiceStreet);
  addField("invoiceZipCity", data.invoiceZipCity);

  // Utility-specific fields
  if (isGas && data.gasDetails) {
    addField(
      "gas_previous_provider_name",
      data.gasDetails.previous_provider_name
    );
    addField(
      "elec_previous_customer_number",
      data.gasDetails.previous_customer_number
    ); // Note: Gas mapping uses 'elec_previous_customer_number'
    addField(
      "gas_annual_consumption_kwh",
      data.gasDetails.annual_consumption_kwh
    );
    addField("gas_meter_point_id", data.gasDetails.meter_point_id);
    addField("gas_meter_number", data.gasDetails.meter_number);
    addField("gas_meter_reading", data.gasDetails.meter_reading);
    addField("gas_unit_price_ct_kwh", data.gasDetails.unit_price_ct_kwh);
    addField("gas_base_price_eur_month", data.gasDetails.base_price_eur_month);
    // Format delivery date to dd.mm.yy (short year to fit PDF field)
    addField(
      "gas_delivery_start_date",
      formatDateForPdfShortYear(data.gasDetails.delivery_start_date ?? "")
    );
    addField(
      "gas_desired_monthly_payment",
      data.gasDetails.desired_monthly_payment
    );
  } else if (!isGas && data.electricityDetails) {
    addField(
      "elec_previous_provider_name",
      data.electricityDetails.previous_provider_name
    );
    addField(
      "elec_previous_customer_number",
      data.electricityDetails.previous_customer_number
    );
    addField(
      "elec_annual_consumption_kwh",
      data.electricityDetails.annual_consumption_kwh
    );
    addField("elec_meter_point_id", data.electricityDetails.meter_point_id);
    addField("elec_meter_number", data.electricityDetails.meter_number);
    addField("elec_meter_reading", data.electricityDetails.meter_reading);
    addField(
      "elec_unit_price_ct_kwh",
      data.electricityDetails.unit_price_ct_kwh
    );
    addField(
      "elec_base_price_eur_month",
      data.electricityDetails.base_price_eur_month
    );
    // Format delivery date to dd.mm.yyyy
    addField(
      "elec_delivery_start_date",
      formatDateForPdf(data.electricityDetails.delivery_start_date ?? "")
    );
    addField(
      "elec_desired_monthly_payment",
      data.electricityDetails.desired_monthly_payment
    );
  }

  return fieldsToFill;
}

/**
 * Generates a filled PDF contract from customer data
 */
export async function generateContractPdf(
  data: ContractPdfData,
  templatePath: string
): Promise<PdfGenerationResult> {
  const templateType = inferTemplateType(templatePath, data.contractType);

  try {
    // Load the PDF template
    const templateBytes = await loadPdfTemplate(templatePath);
    const pdfDoc = await PDFDocument.load(templateBytes);

    // Integrity check before writing any field values.
    const integrity = validateTemplateIntegrity(pdfDoc, templateType);
    if (!integrity.valid) {
      const details = integrity.issues.join(" | ");
      console.error("PDF_TEMPLATE_INTEGRITY_FAILED", {
        templateType,
        details,
      });
      return buildPdfFailure(
        templateType,
        PDF_TEMPLATE_INTEGRITY_FAILED,
        "PDF template integrity check failed",
        details
      );
    }

    // Get the form from the PDF
    const form = pdfDoc.getForm();
    const fields = form.getFields();

    console.log(
      "PDF Fields available:",
      fields.map((f) => f.getName())
    );
    console.log("Total fields:", fields.length);

    // Load the field mapping for this contract type
    const fieldMapping = await loadFieldMapping(data.contractType);
    console.log("Field mapping loaded:", fieldMapping);

    // Map contract data to PDF fields using the mapping
    const fieldsToFill = mapDataToFields(data, fieldMapping, data.contractType);
    console.log("Fields to fill:", fieldsToFill);

    // Fill in all text fields
    const fieldNamesSet = new Set(fields.map((f) => f.getName()));
    const missingFieldNames: string[] = [];
    const fillErrors: string[] = [];
    let filledCount = 0;

    for (const [fieldName, value] of Object.entries(fieldsToFill)) {
      if (!fieldNamesSet.has(fieldName)) {
        missingFieldNames.push(fieldName);
        continue;
      }

      try {
        const field = form.getTextField(fieldName);
        field.setText(value);
        console.log(`✓ Filled field: ${fieldName} = ${value}`);
        filledCount++;
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        console.warn(`Could not fill field: ${fieldName}`, error);
        fillErrors.push(`${fieldName}: ${errorMessage}`);
      }
    }

    console.log(
      `Successfully filled ${filledCount} out of ${
        Object.keys(fieldsToFill).length
      } attempted fields`
    );

    // Handle checkbox fields (e.g., gender selection)
    if (
      fieldMapping["gender_male_checkbox"] &&
      fieldMapping["gender_female_checkbox"]
    ) {
      const maleCheckboxName = fieldMapping["gender_male_checkbox"];
      const femaleCheckboxName = fieldMapping["gender_female_checkbox"];

      console.log("Processing gender checkboxes:", {
        gender: data.gender,
        maleField: maleCheckboxName,
        femaleField: femaleCheckboxName,
      });

      if (
        !fieldNamesSet.has(maleCheckboxName) ||
        !fieldNamesSet.has(femaleCheckboxName)
      ) {
        fillErrors.push(
          `Missing gender checkboxes: ${maleCheckboxName}, ${femaleCheckboxName}`
        );
      } else {
        try {
          const maleCheckbox = form.getCheckBox(maleCheckboxName);
          const femaleCheckbox = form.getCheckBox(femaleCheckboxName);

          // Reset first so repeated runs/retries always produce deterministic state.
          maleCheckbox.uncheck();
          femaleCheckbox.uncheck();

          // 1 = Herr (male), 2 = Frau (female), 3 = Divers (no checkbox)
          if (data.gender === 1) {
            maleCheckbox.check();
            console.log(`✓ Checked male checkbox: ${maleCheckboxName}`);
          } else if (data.gender === 2) {
            femaleCheckbox.check();
            console.log(`✓ Checked female checkbox: ${femaleCheckboxName}`);
          } else {
            console.log(
              "Gender is Divers or not set - leaving both checkboxes unchecked"
            );
          }

          // Workaround for pdf-lib flattening with indirect AP dictionaries:
          // force direct appearance dictionaries so the selected state is rendered.
          maleCheckbox.defaultUpdateAppearances();
          femaleCheckbox.defaultUpdateAppearances();
        } catch (error) {
          fillErrors.push(
            `Gender checkbox error: ${extractErrorMessage(error)}`
          );
        }
      }
    }

    const filledValidation = validateFilledOutput({
      attemptedFieldCount: Object.keys(fieldsToFill).length,
      filledFieldCount: filledCount,
      missingFieldNames,
      fillErrors,
    });

    if (!filledValidation.valid) {
      const details = filledValidation.issues.join(" | ");
      console.error("PDF_FILLED_OUTPUT_INVALID", {
        templateType,
        details,
      });
      return buildPdfFailure(
        templateType,
        PDF_FILLED_OUTPUT_INVALID,
        "PDF field fill validation failed",
        details
      );
    }

    // Generate text-field appearances first so all viewers render values
    // consistently. Checkboxes keep their template-specific appearances.
    try {
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      for (const field of form.getFields()) {
        if (field instanceof PDFTextField && field.needsAppearancesUpdate()) {
          field.defaultUpdateAppearances(font);
        }

        if (field instanceof PDFCheckBox && field.needsAppearancesUpdate()) {
          field.defaultUpdateAppearances();
        }
      }
    } catch (error) {
      const details = extractErrorMessage(error);
      console.error("PDF_FIELD_APPEARANCE_FAILED", {
        templateType,
        details,
      });
      return buildPdfFailure(
        templateType,
        PDF_FIELD_APPEARANCE_FAILED,
        "PDF appearance generation failed",
        details
      );
    }

    // Flatten must succeed for Stadtwerke reliability requirements.
    try {
      form.flatten({ updateFieldAppearances: false });
      console.log("PDF form fields flattened for compatibility");
    } catch (error) {
      const details = extractErrorMessage(error);
      console.error("PDF_FLATTEN_FAILED", {
        templateType,
        details,
      });
      return buildPdfFailure(
        templateType,
        PDF_FLATTEN_FAILED,
        "PDF flattening failed",
        details
      );
    }

    // Embed signature image at specified coordinates
    try {
      if (data.signatureBase64) {
        console.log("Embedding signature at specified coordinates...");

        // Get signature image bytes
        const signatureBytes = dataUrlToArrayBuffer(data.signatureBase64);

        // Embed the signature image in the PDF
        const signatureImage = await pdfDoc.embedPng(signatureBytes);

        // Get all pages
        const pages = pdfDoc.getPages();

        // Define signature positions on the first page
        // Position 1: x: 380, y: 286, width: 82, height: 22 - SEPA mandate (only if IBAN exists)
        // Position 2: x: 450, y: 92, width: 82, height: 22 - Contract signature (always)
        const signaturePositions =
          data.bankDetails && data.bankDetails.trim() !== ""
            ? [
                { x: 380, y: 286, width: 82, height: 22 }, // SEPA mandate signature
                { x: 450, y: 92, width: 82, height: 22 }, // Contract signature
              ]
            : [
                { x: 450, y: 92, width: 82, height: 22 }, // Contract signature only
              ];

        const firstPage = pages[0];
        if (!firstPage) {
          throw new Error("PDF has no pages");
        }

        for (const position of signaturePositions) {
          firstPage.drawImage(signatureImage, {
            x: position.x,
            y: position.y,
            width: position.width,
            height: position.height,
          });
          console.log(
            `✓ Signature embedded at position (${position.x}, ${position.y})`
          );
        }

        console.log("✓ All signatures embedded successfully");
      } else {
        console.warn("No signature data provided");
      }
    } catch (error) {
      console.warn("Could not embed signature image:", error);
      // Continue even if signature embedding fails
    }

    // Embed employee signature (for Stadtwerke Dessau contracts)
    try {
      if (data.employeeSignatureBase64) {
        console.log("Embedding employee signature at specified coordinates...");

        const employeeSignatureBytes = dataUrlToArrayBuffer(
          data.employeeSignatureBase64
        );

        const employeeSignatureImage = await pdfDoc.embedPng(
          employeeSignatureBytes
        );

        const pages = pdfDoc.getPages();
        const firstPage = pages[0];

        if (!firstPage) {
          throw new Error("PDF has no pages");
        }

        const employeeSignaturePosition = {
          x: 450,
          y: 732,
          width: 75,
          height: 25,
        };

        firstPage.drawImage(employeeSignatureImage, {
          x: employeeSignaturePosition.x,
          y: employeeSignaturePosition.y,
          width: employeeSignaturePosition.width,
          height: employeeSignaturePosition.height,
        });

        console.log(
          `✓ Employee signature embedded at position (${employeeSignaturePosition.x}, ${employeeSignaturePosition.y})`
        );
      } else {
        console.log("No employee signature data provided");
      }
    } catch (error) {
      console.warn("Could not embed employee signature image:", error);
      // Continue even if employee signature embedding fails
    }

    // Save the PDF
    const pdfBytes = await pdfDoc.save();

    // Convert to Blob
    const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });

    return {
      success: true,
      pdfBlob,
      templateType,
    };
  } catch (error) {
    console.error("Error generating PDF:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Unknown error generating PDF",
      templateType,
      errorCode: "PDF_GENERATION_FAILED",
      errorDetails: extractErrorMessage(error),
    };
  }
}

/**
 * Converts a Blob to base64 string for API transmission
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        // Remove the data URL prefix to get just the base64 data
        const base64 = reader.result.split(",")[1] || "";
        resolve(base64);
      } else {
        reject(new Error("Failed to convert blob to base64"));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
